<template>
  <v-dialog :value="value" max-width="480" @input="$emit('input', $event)">
    <v-card class="topup-card">
      <v-card-title class="topup-card__title">
        Top up credits
        <v-btn icon small class="topup-card__close" aria-label="Close" @click="$emit('input', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div class="topup-agent-switch" role="tablist" aria-label="Choose which agent to top up">
          <button
            v-for="agent in TRACEY_AGENTS"
            :key="agent.key"
            type="button"
            role="tab"
            class="topup-agent-switch__btn"
            :class="{
              'topup-agent-switch__btn--active': selectedAgent === agent.key,
              [`topup-agent-switch__btn--${agent.key}`]: selectedAgent === agent.key,
            }"
            :aria-selected="selectedAgent === agent.key"
            @click="selectAgent(agent.key)"
          >
            {{ agent.name }}
          </button>
        </div>

        <p class="topup-card__sub">
          Extra {{ currentAgent.name }} credits, on top of your monthly allowance.
          {{ currentAgent.name }} and {{ otherAgent.name }} have separate wallets --
          credits can't be transferred between them.
        </p>

        <p v-if="errorMessage" class="topup-card__error">{{ errorMessage }}</p>

        <div v-if="tiersLoading" class="topup-card__loading">Loading tiers…</div>
        <div v-else class="topup-card__tiers">
          <div v-for="tier in tiers" :key="tier.credits" class="topup-tier">
            <div class="topup-tier__credits">{{ tier.credits.toLocaleString() }} <span>credits</span></div>
            <div class="topup-tier__price">{{ tier.price }} AUD</div>
            <v-btn
              block
              small
              depressed
              :color="selectedAgent === 'ultra' ? '#ff1463' : '#0891b2'"
              dark
              class="topup-tier__buy"
              :loading="buyingCredits === tier.credits"
              :disabled="buyingCredits !== null"
              @click="buy(tier)"
            >
              Buy
            </v-btn>
          </div>
        </div>

        <p class="topup-card__note">
          You'll be taken to Stripe to complete payment securely.
        </p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { fetchTraceyTopupTiers, createTraceyTopupCheckout } from '../APIManager.js';
import { TRACEY_AGENTS, agentByKey } from './agentVersions.js';

// Shown until the tiers finish loading from the server, so the modal isn't
// empty on first paint. Kept in sync with TRACEY_TOPUP_TIERS in flask.cfg.
const DEFAULT_TIERS = {
  ultra: [
    { credits: 200, price: '50' },
    { credits: 500, price: '125' },
    { credits: 1000, price: '250' },
    { credits: 2000, price: '500' },
  ],
  sprint: [
    { credits: 100, price: '25' },
    { credits: 200, price: '50' },
    { credits: 400, price: '100' },
    { credits: 800, price: '200' },
  ],
};

export default {
  name: 'TraceyCreditsTopupModal',
  props: {
    value: { type: Boolean, default: false },
    // Which agent's tiers to show first when the modal opens -- normally
    // whichever agent is currently selected in chat. Still freely
    // switchable inside the modal via the tab control.
    defaultAgent: { type: String, default: 'ultra' },
  },
  data() {
    return {
      TRACEY_AGENTS,
      selectedAgent: this.defaultAgent,
      tiersByAgent: { ...DEFAULT_TIERS },
      tiersLoading: false,
      buyingCredits: null, // the tier currently being purchased, or null
      errorMessage: '',
    };
  },
  computed: {
    tiers() {
      return this.tiersByAgent[this.selectedAgent] || [];
    },
    currentAgent() {
      return agentByKey(this.selectedAgent);
    },
    otherAgent() {
      return agentByKey(this.selectedAgent === 'ultra' ? 'sprint' : 'ultra');
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.errorMessage = '';
        this.selectedAgent = this.defaultAgent;
        this.loadTiers(this.selectedAgent);
      }
    },
  },
  methods: {
    selectAgent(key) {
      if (key === this.selectedAgent) return;
      this.selectedAgent = key;
      this.errorMessage = '';
      this.loadTiers(key);
    },
    async loadTiers(agentVersion) {
      this.tiersLoading = true;
      try {
        const tiers = await fetchTraceyTopupTiers(agentVersion);
        if (tiers && tiers.length) {
          this.tiersByAgent = { ...this.tiersByAgent, [agentVersion]: tiers };
        }
      } catch (e) {
        // Non-fatal: the default tiers stay shown, purchase still validates
        // server-side against the real configured list.
        console.warn(`Could not load Tracey ${agentVersion} top-up tiers:`, e);
      } finally {
        this.tiersLoading = false;
      }
    },
    async buy(tier) {
      this.buyingCredits = tier.credits;
      this.errorMessage = '';
      try {
        const { checkout_url: checkoutUrl } = await createTraceyTopupCheckout(this.selectedAgent, tier.credits);
        if (!checkoutUrl) throw new Error('No checkout URL returned');
        // Leaves the SPA entirely -- credits are granted once Stripe
        // confirms payment and calls the backend webhook, then the user
        // lands back on /usage?topup=success (see UsagePage.vue).
        window.location.href = checkoutUrl;
      } catch (e) {
        this.errorMessage = 'Could not start checkout. Please try again.';
        this.buyingCredits = null;
      }
    },
  },
};
</script>

<style scoped>
.topup-card__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 17px;
  font-weight: 700;
  padding-bottom: 4px;
}

.topup-card__close {
  margin-right: -8px;
}

.topup-agent-switch {
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: #f2f4f7;
  margin-bottom: 14px;
}

.topup-agent-switch__btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 16px;
  border-radius: 8px;
  color: #667085;
  transition: background 0.15s, color 0.15s;
}

.topup-agent-switch__btn--active {
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}

.topup-agent-switch__btn--ultra.topup-agent-switch__btn--active {
  color: #a3134f;
}

.topup-agent-switch__btn--sprint.topup-agent-switch__btn--active {
  color: #075985;
}

.topup-card__sub {
  font-size: 13.5px;
  color: #667085;
  line-height: 1.5;
  margin: 0 0 16px;
}

.topup-card__error {
  font-size: 13px;
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
  border-radius: 8px;
  padding: 8px 12px;
  margin: 0 0 12px;
}

.topup-card__loading {
  font-size: 13px;
  color: #98a2b3;
  padding: 20px 0;
  text-align: center;
}

.topup-card__tiers {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.topup-tier {
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 14px;
  text-align: center;
}

.topup-tier__credits {
  font-size: 18px;
  font-weight: 700;
  color: #101828;
}

.topup-tier__credits span {
  font-size: 12px;
  font-weight: 500;
  color: #98a2b3;
}

.topup-tier__price {
  margin: 2px 0 10px;
  font-size: 13px;
  color: #667085;
}

.topup-tier__buy {
  text-transform: none;
  letter-spacing: 0;
}

.topup-card__note {
  margin: 14px 0 0;
  font-size: 12px;
  color: #98a2b3;
  text-align: center;
}
</style>
